import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '@/stores/auth.store';

const BASE_URL = '/api/v1';

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
});

// ── Request interceptor: attach JWT + tenant header ─────────────────────────
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken, schoolSlug, user } = useAuthStore.getState();
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  // Skip tenant header on platform-level endpoints
  const url = config.url ?? '';
  const isSuperadminPath =
    url.includes('/auth/superadmin') || url.includes('/superadmin');
  // Fall back to the user's own schoolSlug if the top-level one is missing.
  const tenantCode = schoolSlug ?? user?.schoolSlug ?? null;
  if (tenantCode && !isSuperadminPath) {
    config.headers.set('X-School-Code', tenantCode);
  }
  return config;
});

// ── Response interceptor: refresh on 401 once ──────────────────────────────
let isRefreshing = false;
let queue: Array<(token: string | null) => void> = [];

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // An org-scoped token hit a tenant route with no school selected yet —
    // send the user to pick one instead of showing a generic 403.
    if (
      (error.response?.data as any)?.error?.code === 'SCHOOL_NOT_SELECTED' &&
      typeof window !== 'undefined'
    ) {
      window.location.assign('/org/select-school');
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.includes('/auth/refresh') &&
      !original.url?.includes('/auth/login')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push((token) => {
            if (token) {
              original.headers.set('Authorization', `Bearer ${token}`);
              resolve(api(original));
            } else {
              reject(error);
            }
          });
        });
      }

      original._retry = true;
      isRefreshing = true;
      try {
        const refreshToken = useAuthStore.getState().refreshToken;
        if (!refreshToken) throw new Error('No refresh token');

        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, {
          refreshToken,
        });
        const newAccess = data?.data?.tokens?.accessToken as
          | string
          | undefined;
        const newRefresh = data?.data?.tokens?.refreshToken as
          | string
          | undefined;
        if (!newAccess) throw new Error('Refresh failed');

        useAuthStore.getState().setTokens(newAccess, newRefresh ?? refreshToken);
        queue.forEach((cb) => cb(newAccess));
        queue = [];
        isRefreshing = false;

        original.headers.set('Authorization', `Bearer ${newAccess}`);
        return api(original);
      } catch (refreshErr: any) {
        queue.forEach((cb) => cb(null));
        queue = [];
        isRefreshing = false;
        // Diagnostic trail for the recurring "gets logged out" report —
        // this is the ONE place that actually performs the hard logout +
        // redirect to /login. Logging exactly which original request
        // 401'd and why the refresh itself failed turns the next
        // occurrence into hard evidence instead of another guess.
        // eslint-disable-next-line no-console
        console.error('[axios] refresh failed — logging out', {
          originalUrl: original?.url,
          refreshStatus: refreshErr?.response?.status,
          refreshCode: refreshErr?.response?.data?.error?.code,
          refreshMessage:
            refreshErr?.response?.data?.error?.message ?? refreshErr?.message,
        });
        useAuthStore.getState().logout();
        if (typeof window !== 'undefined') {
          window.location.assign('/login');
        }
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
